export interface AmazonOAuthCallbackDraft {
  code: string | null
  spapiOauthCode: string | null
  state: string | null
  sellingPartnerId: string | null
  error: string | null
  errorDescription: string | null
  capturedAt: number
}

export type AmazonOAuthCallbackParams = Omit<AmazonOAuthCallbackDraft, 'capturedAt'>

const STORAGE_KEY = 'tlaaq_amazon_oauth_callback'
const CALLBACK_MAX_AGE_MS = 30 * 60 * 1000

const EMPTY_CALLBACK: AmazonOAuthCallbackParams = {
  code: null,
  spapiOauthCode: null,
  state: null,
  sellingPartnerId: null,
  error: null,
  errorDescription: null,
}

function canUseSessionStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined'
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function normalizeDraft(value: unknown): AmazonOAuthCallbackDraft | null {
  if (!isRecord(value)) return null
  const capturedAt = typeof value.capturedAt === 'number' ? value.capturedAt : Date.now()
  if (Date.now() - capturedAt > CALLBACK_MAX_AGE_MS) return null
  const draft: AmazonOAuthCallbackDraft = {
    code: typeof value.code === 'string' ? value.code : null,
    spapiOauthCode: typeof value.spapiOauthCode === 'string' ? value.spapiOauthCode : null,
    state: typeof value.state === 'string' ? value.state : null,
    sellingPartnerId: typeof value.sellingPartnerId === 'string' ? value.sellingPartnerId : null,
    error: typeof value.error === 'string' ? value.error : null,
    errorDescription: typeof value.errorDescription === 'string' ? value.errorDescription : null,
    capturedAt,
  }
  return hasCallbackParams(draft) ? draft : null
}

function paramsFromUrl(url: URL): AmazonOAuthCallbackParams {
  return {
    code: url.searchParams.get('code'),
    spapiOauthCode: url.searchParams.get('spapi_oauth_code') ?? url.searchParams.get('spapiOauthCode'),
    state: url.searchParams.get('state'),
    sellingPartnerId: url.searchParams.get('selling_partner_id') ?? url.searchParams.get('sellingPartnerId'),
    error: url.searchParams.get('error'),
    errorDescription: url.searchParams.get('error_description'),
  }
}

function isAmazonOAuthUrl(url: URL): boolean {
  const normalizedPath = url.pathname.replace(/\/+$/, '') || '/'
  return normalizedPath === '/amazon-oauth'
}

export function hasCallbackParams(params: AmazonOAuthCallbackParams): boolean {
  return Boolean(params.code || params.spapiOauthCode || params.state || params.sellingPartnerId || params.error || params.errorDescription)
}

export function saveAmazonOAuthCallback(params: AmazonOAuthCallbackParams): AmazonOAuthCallbackDraft | null {
  if (!hasCallbackParams(params)) return null
  const draft: AmazonOAuthCallbackDraft = { ...params, capturedAt: Date.now() }
  if (!canUseSessionStorage()) return draft
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(draft))
  } catch {
    return draft
  }
  return draft
}

export function captureAmazonOAuthCallbackFromUrl(href: string = typeof window === 'undefined' ? '' : window.location.href): AmazonOAuthCallbackDraft | null {
  if (!href) return null
  const url = new URL(href)
  if (!isAmazonOAuthUrl(url)) return null
  return saveAmazonOAuthCallback(paramsFromUrl(url))
}

export function readStoredAmazonOAuthCallback(): AmazonOAuthCallbackDraft | null {
  if (!canUseSessionStorage()) return null
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const draft = normalizeDraft(JSON.parse(raw))
    if (!draft) window.sessionStorage.removeItem(STORAGE_KEY)
    return draft
  } catch {
    return null
  }
}

export function hasStoredAmazonOAuthCallback(): boolean {
  return readStoredAmazonOAuthCallback() !== null
}

export function readAmazonOAuthCallbackParams(): AmazonOAuthCallbackParams {
  if (typeof window !== 'undefined') {
    const url = new URL(window.location.href)
    const fromUrl = paramsFromUrl(url)
    if (isAmazonOAuthUrl(url) && hasCallbackParams(fromUrl)) {
      saveAmazonOAuthCallback(fromUrl)
      return fromUrl
    }
  }

  const stored = readStoredAmazonOAuthCallback()
  if (stored) {
    return {
      code: stored.code,
      spapiOauthCode: stored.spapiOauthCode,
      state: stored.state,
      sellingPartnerId: stored.sellingPartnerId,
      error: stored.error,
      errorDescription: stored.errorDescription,
    }
  }

  return EMPTY_CALLBACK
}

export function clearAmazonOAuthCallback(): void {
  if (!canUseSessionStorage()) return
  try {
    window.sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // Ignore storage failures; the callback URL is stripped after success too.
  }
}
